import { calculateNavBarMaxWidth } from "../utils/pdfScalingUtils";
import { useMemo, useRef, useEffect, useState } from "react";

/**
 * Hook for PDF navigation bar width calculation
 * Provides reactive width calculation based on PDF canvas container size
 */
const useNavBarWidth = (canvasRef) => {
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });
  const resizeObserverRef = useRef(null);

  // Set up ResizeObserver to track container size changes
  useEffect(() => {
    if (!canvasRef?.current) return;

    const canvas = canvasRef.current;
    let container = canvas.parentElement;

    // Find the PDF container
    while (
      container &&
      !container.getAttribute("class")?.includes("MuiCardContent")
    ) {
      container = container.parentElement;
    }

    if (!container) {
      container = canvas.parentElement;
    }

    if (!container) return;

    // Create ResizeObserver to watch container size changes
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setContainerSize({ width, height });
      }
    });

    // Start observing the container
    resizeObserverRef.current.observe(container);

    // Initial size measurement
    const rect = container.getBoundingClientRect();
    setContainerSize({ width: rect.width, height: rect.height });

    // Cleanup observer on unmount
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
    };
  }, [canvasRef]);

  /**
   * Calculate maximum width for navigation bar
   * Reactive to container size changes
   */
  const maxNavBarWidth = useMemo(() => {
    return calculateNavBarMaxWidth(canvasRef?.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvasRef, containerSize.width, containerSize.height]);

  return {
    maxNavBarWidth,
  };
};

export default useNavBarWidth;
